// Subscription-level DSM templates against the real backend.
//
// Templates are shared across a subscription (so they sync between browsers /
// users), unlike a project's scope doc. They live as their own records in
// `feature_design_scope_matrix` with `project_id = NULL` and `subscription_id`
// set — written and read with the SUBSCRIPTION token (services/auth's access
// token), so no project id is involved. `data.source = "template"` marks the
// record as a template (vs a project's `source = "project"`).

import { GRAPHQL_API_URL } from "config/config";
import { BASE_URL as GATEWAY_BASE_URL } from "services/endpoints";
import { getAccessToken } from "services/auth";
import type { DsmTemplate, TemplateItem, TemplateSource } from "./types";
import { migrateTemplateItem } from "./classification";

const DSM_DOC_URL = `${GATEWAY_BASE_URL}/v1/dsm/doc`;
const SCHEMA_VERSION = 3;

// Interior envelope of a template record. `source` discriminates it from a
// project scope doc; `origin` is where the template came from (the public
// DsmTemplate.source). `deleted` is a soft-delete tombstone — the backend has
// no DELETE endpoint yet, so a removed template is flagged and hidden on read.
interface TemplateEnvelope {
  schemaVersion: number;
  source: "template";
  origin: TemplateSource;
  name: string;
  items: TemplateItem[];
  createdAt: string;
  deleted?: boolean;
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (!token) throw new Error("DSM templates: no subscription token available");
  return {
    "content-type": "application/json",
    Authorization: `Bearer ${token}`
  };
}

// The subscription token's RLS only returns subscription-scoped rows (project_id
// null), so no explicit filter is needed; we still keep source === "template"
// as a guard against any other subscription-level record shape.
const LIST_QUERY = `query getDsmTemplates {
  feature_design_scope_matrix { id data }
}`;

interface TemplateRow {
  id: string;
  env: TemplateEnvelope;
}

async function fetchRows(): Promise<TemplateRow[]> {
  const res = await fetch(`${GRAPHQL_API_URL}?op=getDsmTemplates`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      operationName: "getDsmTemplates",
      query: LIST_QUERY
    })
  });
  const json = await res.json();
  if (json.errors) throw new Error(JSON.stringify(json.errors));
  const rows: Array<{ id: string; data: unknown }> =
    json.data?.feature_design_scope_matrix ?? [];
  return rows
    .map((r) => ({ id: r.id, env: r.data as TemplateEnvelope }))
    .filter((r) => r.env?.source === "template");
}

const toTemplate = (id: string, env: TemplateEnvelope): DsmTemplate => ({
  id,
  name: env.name,
  source: env.origin,
  items: Array.isArray(env.items) ? env.items.map(migrateTemplateItem) : [],
  createdAt: env.createdAt
});

/** All live (non-tombstoned) subscription templates. */
export async function loadTemplates(): Promise<DsmTemplate[]> {
  const rows = await fetchRows();
  return rows.filter((r) => !r.env.deleted).map((r) => toTemplate(r.id, r.env));
}

/** A single live template by record id. */
export async function loadTemplate(
  id: string
): Promise<DsmTemplate | undefined> {
  const rows = await fetchRows();
  const row = rows.find((r) => r.id === id && !r.env.deleted);
  return row ? toTemplate(row.id, row.env) : undefined;
}

/**
 * Origins present across ALL template records, including tombstoned ones — so
 * mock seeding stays idempotent and never resurrects a mock the user deleted.
 */
export async function templateOriginsPresent(): Promise<Set<TemplateSource>> {
  const rows = await fetchRows();
  return new Set(rows.map((r) => r.env.origin));
}

/** Create a new subscription-level template (POST → new record). */
export async function createTemplateRecord(
  name: string,
  items: TemplateItem[],
  origin: TemplateSource
): Promise<DsmTemplate> {
  const env: TemplateEnvelope = {
    schemaVersion: SCHEMA_VERSION,
    source: "template",
    origin,
    name,
    items,
    createdAt: new Date().toISOString()
  };
  const res = await fetch(DSM_DOC_URL, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ data: env })
  });
  if (!res.ok) throw new Error(`DSM template create failed (${res.status})`);
  const json = await res.json();
  return toTemplate((json.id as string) ?? "", env);
}

/** Soft-delete: no DELETE endpoint yet, so tombstone via PUT and hide on read. */
export async function softDeleteTemplateRecord(id: string): Promise<void> {
  const rows = await fetchRows();
  const row = rows.find((r) => r.id === id);
  if (!row) return;
  const env: TemplateEnvelope = { ...row.env, deleted: true };
  const res = await fetch(`${DSM_DOC_URL}/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ data: env })
  });
  if (!res.ok) throw new Error(`DSM template delete failed (${res.status})`);
}
